import { Loader } from '@/components/Loader/Loader';
import { COA_LINKS } from '@/components/ChartOfAccounts/ChartOfAccounts.helpers';
import { COAProvider } from '@/context/coa-context';
import {
  getAccountTypes,
  getClientAccountNumbers,
  getCountries,
} from '@/core/api';
import {
  activeCompanySelector,
  queryKeySelector,
} from '@/core/store/user/userSlice';
import { DefaultAccWithType } from '@/core/types';
import { useAppSelector } from '@/hooks';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router';

export const COAWrapper = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const activeCompany = useAppSelector(activeCompanySelector);
  const queryKey = useAppSelector(queryKeySelector);

  const { data: countries, isLoading: isLoadingCountries } = useQuery({
    queryKey: ['countries'],
    queryFn: getCountries,
  });

  const { data: accountTypes, isLoading: isLoadingAccountTypes } = useQuery({
    queryKey: ['accountTypes'],
    queryFn: getAccountTypes,
  });

  const {
    data: clientAccountNumbers,
    isLoading: isLoadingClientAccountNumbers,
  } = useQuery({
    queryKey: ['clientAccountNumbers', queryKey],
    queryFn: () => getClientAccountNumbers(activeCompany?.company_id!),
    enabled: !!activeCompany?.company_id,
  });

  const defaultAccountNumbers: DefaultAccWithType[] | undefined =
    useMemo(() => {
      return accountTypes
        ?.map((v) =>
          (v.account_numbers || []).map((a) => ({
            ...a,
            account_number: String(a.account_number),
            account_type: v.account_type,
          }))
        )
        .flat();
    }, [accountTypes]);

  const isLoading =
    isLoadingAccountTypes ||
    isLoadingClientAccountNumbers ||
    isLoadingCountries;

  if (isLoading) return <Loader />;

  return (
    <div>
      <div className={`flex w-full px-4 pb-8 pt-6 lg:px-6 2xl:px-14`}>
        <div className='w-[200px]'>
          <div className='sticky top-16'>
            {COA_LINKS.map((v) => {
              const isActive = location.pathname === v.to;

              return (
                <Link
                  key={v.label}
                  className={classNames('block p-2 font-semibold', {
                    'rounded-lg bg-green bg-opacity-[0.06] text-green':
                      isActive,
                  })}
                  to={v.to}
                >
                  {t(v.label)}
                </Link>
              );
            })}
          </div>
        </div>
        <COAProvider
          accountTypes={accountTypes}
          clientAccountNumbers={clientAccountNumbers}
          defaultAccountNumbers={defaultAccountNumbers}
          countries={countries}
        >
          <Outlet />
        </COAProvider>
      </div>
    </div>
  );
};
